import { create } from 'zustand';
import { inboxService, channelService, type InboxItem, type InboxStats, type InboxFilters, type InboxView } from '@services/index';

interface InboxState {
  // Data
  items: InboxItem[];
  stats: InboxStats | null;
  selectedId: string | null;

  // Pagination
  total: number;
  page: number;
  limit: number;
  totalPages: number;

  // Filters
  filters: InboxFilters;
  view: InboxView;
  searchQuery: string;

  // Loading states
  isLoading: boolean;
  isLoadingStats: boolean;
  isSyncing: boolean;
  syncResult: { success: boolean; messagesProcessed: number; ticketsCreated?: number; error?: string } | null;
  error: string | null;

  // Actions
  fetchInbox: () => Promise<void>;
  syncEmails: () => Promise<void>;
  fetchStats: () => Promise<void>;
  setView: (view: InboxView) => void;
  setFilters: (filters: Partial<InboxFilters>) => void;
  /** View and filters in one fetch — a tab switch that changes both would
   *  otherwise fire two requests and render whichever landed last. */
  setQuery: (query: { view?: InboxView; filters?: Partial<InboxFilters> }) => void;
  setSearchQuery: (query: string) => void;
  setPage: (page: number) => void;
  selectItem: (id: string | null) => void;
  markAsRead: (ticketId: string) => Promise<void>;
  markAsUnread: (ticketId: string) => Promise<void>;
  refreshInbox: () => Promise<void>;
  reset: () => void;
}

const initialFilters: InboxFilters = {
  page: 1,
  limit: 25,
  sortBy: 'sla_priority',
  // Latest mail on top by default; the page can switch back to 'sla'.
  sort: 'newest',
};

// Bumped on every fetchInbox call; a response that lands after a newer request
// started is dropped, so a background auto-refresh can never overwrite the
// results of a tab/filter/search change made while it was in flight.
let inboxRequestSeq = 0;

export const useInboxStore = create<InboxState>()((set, get) => ({
  // Initial state
  items: [],
  stats: null,
  selectedId: null,
  total: 0,
  page: 1,
  limit: 25,
  totalPages: 0,
  filters: initialFilters,
  view: 'all',
  searchQuery: '',
  isLoading: false,
  isLoadingStats: false,
  isSyncing: false,
  syncResult: null,
  error: null,

  // Fetch inbox items
  fetchInbox: async () => {
    const { filters, view, searchQuery } = get();
    const requestId = ++inboxRequestSeq;
    set({ isLoading: true, error: null });

    try {
      const currentFilters: InboxFilters = {
        ...filters,
        view,
        search: searchQuery || undefined,
      };

      const response = await inboxService.getInbox(currentFilters);
      if (requestId !== inboxRequestSeq) return;

      set({
        items: response.data,
        total: response.total,
        page: response.page,
        totalPages: response.totalPages,
        isLoading: false,
      });
    } catch (error) {
      if (requestId !== inboxRequestSeq) return;
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch inbox',
        isLoading: false,
      });
    }
  },

  // Fetch stats
  fetchStats: async () => {
    set({ isLoadingStats: true });

    try {
      const stats = await inboxService.getStats();
      set({ stats, isLoadingStats: false });
    } catch (error) {
      set({ isLoadingStats: false });
    }
  },

  // Sync emails from Microsoft Graph and process with AI
  syncEmails: async () => {
    set({ isSyncing: true, syncResult: null });

    try {
      // Get default email config
      const defaultConfig = await channelService.getDefaultEmailConfig();
      if (!defaultConfig) {
        set({
          isSyncing: false,
          syncResult: { success: false, messagesProcessed: 0, error: 'No default email configuration found' }
        });
        return;
      }

      // Trigger sync AND process with AI
      const result = await channelService.syncAndProcessEmails(defaultConfig.id, false, 50);
      const totalProcessed = result.sync.messagesProcessed + result.processing.processed;

      set({
        isSyncing: false,
        syncResult: {
          success: result.sync.success,
          messagesProcessed: totalProcessed,
          ticketsCreated: result.processing.processed
        }
      });

      // Refresh inbox after sync
      if (totalProcessed > 0) {
        await get().refreshInbox();
      }
    } catch (error) {
      set({
        isSyncing: false,
        syncResult: { success: false, messagesProcessed: 0, error: error instanceof Error ? error.message : 'Sync failed' }
      });
    }
  },

  // Set view (all, assigned_to_me, unassigned, etc.)
  setView: (view) => {
    set((state) => ({
      view,
      page: 1,
      filters: { ...state.filters, page: 1 },
    }));
    get().fetchInbox();
  },

  // Set filters
  setFilters: (newFilters) => {
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
      page: 1,
    }));
    get().fetchInbox();
  },

  // Set view and filters together, then fetch once
  setQuery: ({ view, filters: newFilters }) => {
    set((state) => ({
      view: view ?? state.view,
      page: 1,
      filters: { ...state.filters, ...newFilters, page: 1 },
    }));
    get().fetchInbox();
  },

  // Set search query
  setSearchQuery: (query) => {
    set({ searchQuery: query, page: 1 });
    // Debounce is handled in the component
    get().fetchInbox();
  },

  // Set page
  setPage: (page) => {
    set((state) => ({
      page,
      filters: { ...state.filters, page },
    }));
    get().fetchInbox();
  },

  // Select item
  selectItem: (id) => {
    set({ selectedId: id });
  },

  // Mark as read
  markAsRead: async (ticketId) => {
    try {
      await inboxService.markAsRead(ticketId);
      set((state) => ({
        items: state.items.map((item) =>
          item.id === ticketId ? { ...item, unreadCount: 0 } : item
        ),
      }));
      get().fetchStats();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  },

  // Mark as unread
  markAsUnread: async (ticketId) => {
    try {
      await inboxService.markAsUnread(ticketId);
      set((state) => ({
        items: state.items.map((item) =>
          item.id === ticketId ? { ...item, unreadCount: 1 } : item
        ),
      }));
      get().fetchStats();
    } catch (error) {
      console.error('Failed to mark as unread:', error);
    }
  },

  // Refresh inbox
  refreshInbox: async () => {
    await Promise.all([get().fetchInbox(), get().fetchStats()]);
  },

  // Reset state
  reset: () => {
    set({
      items: [],
      stats: null,
      selectedId: null,
      total: 0,
      page: 1,
      totalPages: 0,
      filters: initialFilters,
      view: 'all',
      searchQuery: '',
      isLoading: false,
      isLoadingStats: false,
      isSyncing: false,
      syncResult: null,
      error: null,
    });
  },
}));
